import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { GetStaticPaths, GetStaticProps } from 'next';

import { getFromAPI } from '~utils/api/common';
import parseQuery from '~utils/api/parseQuery';
import normalizeCinemaMovie, { NormalizedMovie } from '~utils/normalizers/normalizeCinemaMovie';

import { Cinema, Movie } from '~types';

dayjs.extend(timezone);

export const getStaticPaths: GetStaticPaths<{ CinemaId: string }> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

interface GSProps {
  cinema: Cinema;
  initialMovies?: NormalizedMovie[];
  initialDates: string[];
}

const slugRegex = /^[a-zA-Z0-9\-_]+$/;
export const getStaticProps: GetStaticProps<GSProps> = async (ctx) => {
  if (!`${ctx?.params?.slug}`.match(slugRegex)) return { notFound: true };
  const cinema = await getFromAPI<Partial<Cinema>>(`/cinemas/${ctx?.params?.slug}`);
  const dates = await getFromAPI(`/shows/dates?CinemaId=${cinema?.id || ''}`);

  if (!dayjs(`${ctx?.params?.date}`).isValid()) {
    return {
      notFound: true,
    };
  }
  const date = dayjs(`${ctx?.params?.date}`).tz(cinema?.timezone || 'America/Hermosillo');

  if (date.isBefore(dayjs(), 'day')) {
    return {
      notFound: true,
    };
  }
  const parsedDate = date.format();

  const initialBillboard = await getFromAPI(
    `/shows?${parseQuery({ CinemaId: `${cinema?.id}`, date: parsedDate })}`,
  );

  const getMovies = async () => {
    const movies: Movie[] = await Promise.all(
      initialBillboard.map(async ({ id }: { id: string }) => {
        return await getFromAPI<Movie[]>(
          `/movies/${id}?CinemaId=${cinema?.id || ''}&date=${parsedDate}`,
        );
      }),
    );
    return movies.filter((movie) => movie.Shows?.length);
  };

  const movies = await getMovies();

  return {
    props: {
      cinema: cinema as Cinema,
      initialMovies: normalizeCinemaMovie(movies),
      initialDates: dates?.map((item: { date: string }) => item.date),
    },
    revalidate: 60 * 60, // 1 hour
  };
};

export { default } from './index';
